import { Component, input, effect, computed, contentChildren } from '@angular/core';

import { ApChipComponent } from '../chip.component';
import { ChipRole, TypeChip } from '../chip.model';

@Component({
    selector: 'ap-chip-list',
    templateUrl: './chip-list.component.html',
    styleUrls: ['./chip-list.component.scss'],
    host: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        '[attr.role]': 'chipListRole()'
    }
})
export class ApChipListComponent {
    readonly chips = contentChildren(ApChipComponent);
    /**
     * Describe which chip you are using.
     */
    public readonly typeChip = input.required<TypeChip>();

    /**
     * Legend of the `ap-chip-list[role=radiogroup]`.
     * This describes the group of chips, which is important for accessibility. Alternatively you may use `aria-label` or `aria-labelledby` to describe the chips for screenreaders, but these are not visually shown.
     */
    public readonly legend = input<string>();

    constructor() {
        effect(() => {
            const role = this.chipRole();
            this.applyChipRoles(role);
        });

        effect(() => {
            const typeChip = this.typeChip();

            if (typeChip === 'multiple') {
                return;
            }
            this.chips().forEach((chip) => {
                chip.selectionChange.subscribe((selected) => {
                    if (selected) {
                        this.deselectOtherChips(chip);
                    }
                });
            });
        });
    }

    private applyChipRoles(role: ChipRole): void {
        this.chips().forEach((chip) => chip.role.set(role));
    }

    /**
     * Deselect all other chips except chip that is passed
     */
    private deselectOtherChips(exception: ApChipComponent): void {
        this.chips()
            .filter((chip) => chip !== exception)
            .forEach((chip) => {
                chip.selected.set(false);
            });
    }

    /**
     * Get a11y role of chips
     */
    public readonly chipListRole = computed(() => {
        switch (this.typeChip()) {
            case 'multiple':
                return 'listbox';
            case 'dynamic':
                return 'list';
            default:
                return 'radiogroup';
        }
    });

    /**
     * Get a11y role of chips
     */
    public readonly chipRole = computed(() => {
        switch (this.typeChip()) {
            case 'multiple':
                return ChipRole.OPTION;
            case 'dynamic':
                return ChipRole.LISTITEM;
            default:
                return ChipRole.RADIO;
        }
    });
}
